import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { ApplicationsDataTableQuery } from 'generated/resources/datatable.graphql'

import { useDataTable } from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { MoveToPhaseModal } from 'modules/ats/components/MoveToPhaseModal'
import { RejectApplicationsModal } from 'modules/ats/components/RejectApplicationsModal'
import { SendEmailToApplicationsModal } from 'modules/ats/components/SendEmailToApplicationsModal'
import { AddApplicationsToJobPostingModal } from 'modules/ats/components/applications/AddApplicationsToJobPostingModal'
import { useAtsContext } from 'modules/ats/lib/atsContext'
import { useJobDetailsContext } from 'modules/ats/pages/JobsPage/JobDetails'
import RequestUpgradeModal from 'modules/upsell/components/RequestUpgradeModal'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'

type Action = 'disqualify' | 'move_to_phase' | 'email' | 'add_to_job_opening' | undefined

interface Props {
  showRejectedAction?: boolean
}
export const ApplicationTableBulkOptions = ({ showRejectedAction = true }: Props) => {
  const dataTable = useDataTable<ApplicationsDataTableQuery>()
  const { job } = useJobDetailsContext()
  const [action, setAction] = React.useState<Action>()

  const { talentPoolByDefault } = useAtsContext()

  if (dataTable.selectedRows.size === 0) {
    return null
  }

  const selectedApplications = dataTable.data.filter((application) =>
    dataTable.selectedRows.has(application.id.toString())
  )
  const selectedApplicationIds = selectedApplications.map((application) => application.id)

  const talentPoolVisible = selectedApplications.some(
    (application) => !application.candidate?.consentToTalentPool
  )
  const talentPoolValue =
    selectedApplications.some((application) => application.candidate?.consentToTalentPool) ||
    talentPoolByDefault
  const everyCandidateWithoutEmail = selectedApplications.every(
    (application) => !application.candidate?.email
  )

  const onSuccess = () => {
    setAction(undefined)
  }

  return (
    <>
      {action === 'disqualify' &&
        showRejectedAction &&
        (hasFeature('ats_bulk_actions') ? (
          <RejectApplicationsModal
            jobPostingId={job.id}
            selectedApplications={selectedApplications}
            title={i18n.tp('ats.candidates.applications.bulk_disqualify.title', {
              count: selectedApplications.length,
            })}
            onSuccess={onSuccess}
            onClose={() => setAction(undefined)}
            saveToTalentPoolCheckboxValue={talentPoolValue}
            saveToTalentPoolCheckboxVisible={talentPoolVisible}
          />
        ) : (
          <RequestUpgradeModal
            requestReason={ValidReasons.UpgradeFromLegacy}
            requestSource="ats-bulk-actions"
            onClose={() => {
              setAction(undefined)
            }}
          />
        ))}

      {action === 'move_to_phase' &&
        (hasFeature('ats_bulk_actions') ? (
          <MoveToPhaseModal
            jobId={job.id}
            applications={selectedApplications}
            onSuccess={onSuccess}
            onClose={() => setAction(undefined)}
          />
        ) : (
          <RequestUpgradeModal
            requestReason={ValidReasons.UpgradeFromLegacy}
            requestSource="ats-bulk-actions"
            onClose={() => {
              setAction(undefined)
            }}
          />
        ))}

      {action === 'add_to_job_opening' &&
        (hasFeature('ats_bulk_actions') ? (
          <AddApplicationsToJobPostingModal
            applicationIds={selectedApplicationIds}
            onSuccess={() => onSuccess()}
            onClose={() => setAction(undefined)}
          />
        ) : (
          <RequestUpgradeModal
            requestReason={ValidReasons.UpgradeFromLegacy}
            requestSource="ats-bulk-actions"
            onClose={() => {
              setAction(undefined)
            }}
          />
        ))}

      {action === 'email' &&
        (hasFeature('ats_bulk_actions') ? (
          <SendEmailToApplicationsModal
            onSuccess={onSuccess}
            selectedApplications={selectedApplications}
            onClose={() => setAction(undefined)}
          />
        ) : (
          <RequestUpgradeModal
            requestReason={ValidReasons.UpgradeFromLegacy}
            requestSource="ats-bulk-actions"
            onClose={() => {
              setAction(undefined)
            }}
          />
        ))}

      <Box flexDirection="row" alignItems="center" gap="s4" justifyContent="spaceBetween">
        {showRejectedAction && (
          <Button
            type="secondary"
            onClick={() => {
              setAction('disqualify')
            }}
            iconName="deny"
          >
            {i18n.t('ats.candidates.application_view.disqualify')}
          </Button>
        )}

        <PopoverMenu type="list">
          <PopoverMenu.Trigger>
            <Button.Decorative type="secondary" iconName="ellipsis" />
          </PopoverMenu.Trigger>

          <PopoverMenu.Content side="bottom" align="center">
            {!everyCandidateWithoutEmail && (
              <PopoverMenu.TouchableOption
                title={i18n.t('ats.candidates.application_view.send_email')}
                onClick={() => setAction('email')}
              />
            )}
            <PopoverMenu.TouchableOption
              title={i18n.t('ats.candidates.application_view.move_to_phase')}
              onClick={() => setAction('move_to_phase')}
            />
            <PopoverMenu.TouchableOption
              title={i18n.t('ats.candidates.application_view.add_to_job_opening')}
              onClick={() => setAction('add_to_job_opening')}
            />
          </PopoverMenu.Content>
        </PopoverMenu>
      </Box>
    </>
  )
}
